import { useCallback } from 'react';
import { useAppRouter } from '../useAppRouter';
import { usePreviousRoute } from './usePreviousRoute';

/**
 * Returns a stable callback that navigates to the route the user was on
 * before the most recent navigation, falling back to `fallbackUrl` if no
 * prior navigation has occurred in this session.
 *
 * The returned function reads the previous path at call time (not at hook
 * call time), so it is safe to register as a long-lived callback without
 * risk of capturing a stale value.
 */
export const useNavigateToPreviousRoute = (
  fallbackUrl: string,
): (() => Promise<void>) => {
  const router = useAppRouter();
  const previousRouteRef = usePreviousRoute();

  return useCallback(async (): Promise<void> => {
    await router.replace(previousRouteRef.current ?? fallbackUrl);
  }, [router, fallbackUrl, previousRouteRef]);
};
